import { registerSW } from 'virtual:pwa-register';

let updateCallback: (() => void) | null = null;
let applySwUpdate: ((reloadPage?: boolean) => Promise<void>) | null = null;
let serviceWorkerUpdatePending = false;

/** Give up waiting for the new worker to take control rather than hanging the button. */
const CONTROLLER_CHANGE_TIMEOUT_MS = 3000;

export function setPwaUpdateCallback(callback: () => void) {
  updateCallback = callback;
}

export function registerProductionServiceWorker() {
  if (!import.meta.env.PROD) return;

  applySwUpdate = registerSW({
    immediate: true,
    onNeedRefresh() {
      serviceWorkerUpdatePending = true;
      updateCallback?.();
    },
  });
}

/**
 * Ask the browser to re-check `sw.js`.
 *
 * The banner is driven by `version.json`, which is fetched fresh on every check, but the service
 * worker only looks for a new build on navigation. Without this nudge the banner can be right while
 * the worker still has no update waiting — and then there is nothing to activate.
 */
export async function refreshServiceWorkerRegistration(): Promise<void> {
  const registration = await currentRegistration();
  await registration?.update().catch(() => undefined);
}

/**
 * Apply a pending update and reload onto the new build.
 *
 * A plain `location.reload()` is not enough: the active worker serves the precached `index.html`
 * (`navigateFallback`), so the reload boots the *same* bundle, the version check still sees a newer
 * build, and the banner comes straight back. The new worker has to take over first.
 */
export async function applyPendingAppUpdate(): Promise<void> {
  if (applySwUpdate && serviceWorkerUpdatePending) {
    serviceWorkerUpdatePending = false;
    await applySwUpdate();
    // Workbox reloads via the "controlling" listener after skipWaiting.
    return;
  }

  if (await activateWaitingServiceWorker()) {
    // The new worker took control; reload now serves the new build.
    window.location.reload();
    return;
  }

  // No worker update to be had (or no service worker at all). Drop the precache so the reload
  // cannot be answered with the stale build, then reload.
  await dropPrecaches();
  window.location.reload();
}

/** @returns true when a waiting worker was told to activate and took control. */
async function activateWaitingServiceWorker(): Promise<boolean> {
  const registration = await currentRegistration();
  if (!registration) return false;

  await registration.update().catch(() => undefined);
  const waiting = registration.waiting;
  if (!waiting) return false;

  return new Promise<boolean>((resolve) => {
    let settled = false;
    const finish = (activated: boolean) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
      resolve(activated);
    };
    const onControllerChange = () => finish(true);
    const timer = setTimeout(() => finish(false), CONTROLLER_CHANGE_TIMEOUT_MS);

    navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);
    waiting.postMessage({ type: 'SKIP_WAITING' });
  });
}

async function currentRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return null;
  try {
    return (await navigator.serviceWorker.getRegistration()) ?? null;
  } catch {
    return null;
  }
}

/** Removes Workbox's precache buckets only — other caches are not ours to clear. */
async function dropPrecaches(): Promise<void> {
  if (typeof caches === 'undefined') return;
  try {
    const keys = await caches.keys();
    await Promise.all(
      keys.filter((key) => /workbox|precache/i.test(key)).map((key) => caches.delete(key))
    );
  } catch {
    // Cache eviction is best effort: a failed delete must not block the reload.
  }
}
